import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Chip, Kpi, RoleChip, StateBlock } from '@w7/ui'
import { Money } from '@/app/bits'
import { useChildren } from '@/app/queries'
import { count, displayMoney, displayMoneyCompact, percent } from '@/lib/format'
import { limitUsage, round2 } from '@/lib/book'
import { ROLE_LABEL, ROLE_SHORT, isAgent, type Account } from '@/data/types'
import { DownlineTable, STATUS, type SortDirection, type SortId } from './DownlineTable'
import { isDirect } from './settle'

export interface DownlineDetailProps {
  account: Account | null
  self: Account | undefined
  /** Omit to inherit Appearance → Table density. */
  density?: 'compact' | 'default'
  loadingAccount?: boolean
  /** Staff Give/Take grant — owners pass true. */
  allowCreditTransfer?: boolean
  onTransfer: (account: Account, direction: 'give' | 'take', amount?: number) => void
  onSwitchDirect?: () => void
}

/**
 * Right pane of Hierarchy split: identity, KPIs, Give/Take when direct,
 * and this node's children (actions gated by isDirect).
 */
export function DownlineDetail({
  account,
  self,
  density,
  loadingAccount,
  allowCreditTransfer = true,
  onTransfer,
  onSwitchDirect,
}: DownlineDetailProps) {
  const [sort, setSort] = useState<SortId>('username')
  const [direction, setDirection] = useState<SortDirection>('asc')
  const children = useChildren(account?.id)
  const direct = account && self ? isDirect(account, self) : false
  const canGiveTake = direct && allowCreditTransfer

  const childRows = useMemo(() => {
    const rows = children.data ?? []
    const factor = direction === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av =
        sort === 'username'
          ? a.username
          : sort === 'lastActiveAt'
            ? a.lastActiveAt
              ? Date.parse(a.lastActiveAt)
              : null
            : a[sort]
      const bv =
        sort === 'username'
          ? b.username
          : sort === 'lastActiveAt'
            ? b.lastActiveAt
              ? Date.parse(b.lastActiveAt)
              : null
            : b[sort]
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv), 'en-IN') * factor
    })
  }, [children.data, sort, direction])

  if (loadingAccount) {
    return (
      <div className="w7-dl-detail" aria-busy="true">
        <StateBlock icon="downline" title="Loading account">
          Fetching the selected downline node.
        </StateBlock>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="w7-dl-detail">
        <StateBlock icon="tree" title="Select an agent in the tree">
          Browse the outline on the left. Give and Take appear here for your direct children.
        </StateBlock>
      </div>
    )
  }

  const usage = limitUsage(account.exposure, account.creditLimit)

  return (
    <div className="w7-dl-detail">
      <header className="w7-dl-detail-head">
        <div className="w7-dl-detail-ident">
          <div className="w7-dl-detail-ident-top">
            <Link to={`/member/${account.id}`} className="w7-link w7-dl-detail-username">
              {account.username}
            </Link>
            <RoleChip role={account.role} label={ROLE_SHORT[account.role]} />
            <Chip tone={STATUS[account.status].tone} size="sm">
              {STATUS[account.status].label}
            </Chip>
          </div>
          <p className="w7-dl-detail-sub">
            {ROLE_LABEL[account.role]} · {account.displayName}
            {isAgent(account.role)
              ? ` · ${count(account.childCount)} direct · ${count(account.memberCount)} members`
              : null}
          </p>
        </div>
        {canGiveTake ? (
          <div className="w7-dl-detail-actions">
            <Button
              variant="secondary"
              icon="give"
              aria-label={`Give credit to ${account.username}`}
              onClick={() => onTransfer(account, 'give')}
            >
              Give
            </Button>
            <Button
              variant="secondary"
              icon="take"
              aria-label={`Take credit from ${account.username}`}
              onClick={() => onTransfer(account, 'take')}
            >
              Take
            </Button>
          </div>
        ) : null}
      </header>

      {!direct ? (
        <p className="w7-dl-note">
          Credit cannot move directly to {account.username} — only to your direct children.
          {onSwitchDirect ? (
            <>
              {' '}
              <button type="button" className="w7-link" onClick={onSwitchDirect}>
                Switch to Direct only
              </button>{' '}
              to settle your immediate downline.
            </>
          ) : null}
        </p>
      ) : null}

      <div className="w7-grid w7-grid-4 w7-dl-detail-kpis">
        <Kpi
          label="Credit limit"
          value={displayMoneyCompact(account.creditLimit, account.currencyType)}
          exact={displayMoney(account.creditLimit, account.currencyType)}
        />
        <Kpi
          label="Balance"
          value={displayMoneyCompact(account.balance, account.currencyType)}
          exact={displayMoney(account.balance, account.currencyType)}
        />
        <Kpi
          label="Exposure"
          value={displayMoneyCompact(account.exposure, account.currencyType)}
          exact={displayMoney(account.exposure, account.currencyType)}
          tone={usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'}
          meta={`${percent(usage, 0)} of limit`}
        />
        <Kpi
          label="Settlement"
          value={<Money value={round2(account.settlement)} signed currency={account.currencyType} alreadyDisplay />}
          exact={displayMoney(account.settlement, account.currencyType)}
          tone={account.settlement > 0 ? 'profit' : account.settlement < 0 ? 'loss' : 'neutral'}
        />
      </div>

      <div className="w7-dl-detail-children">
        <h3 className="w7-h3">Direct children</h3>
        <DownlineTable
          rows={childRows}
          totals={undefined}
          self={self}
          loading={children.isLoading}
          density={density}
          sort={sort}
          direction={direction}
          onSort={(id, next) => {
            setSort(id)
            setDirection(next)
          }}
          onTransfer={onTransfer}
          empty={
            <StateBlock icon="downline" title="No direct downline">
              {account.username} has nobody beneath them yet.
            </StateBlock>
          }
        />
      </div>
    </div>
  )
}
